import { useLabelVersion } from '../../../../../features/labels/LabelsProvider';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SummaryV3CageMap from "./components/SummaryV3CageMap";
import SummaryV3CenterTable from "./components/SummaryV3CenterTable";
import SummaryV3Distribution from "./components/SummaryV3Distribution";
import SummaryV3Kpis from "./components/SummaryV3Kpis";
import { translateText } from "../../../shared/utils/historyTranslation";
import {
  canViewSensorFailures,
  getCenterCriticalityScore,
  getMapCenterRank,
  getCenterSummary,
  getSimpleDistribution,
  getSummaryDashboardStats,
  isCenterHealthy,
} from "./utils/summaryDashboardV3Utils";
import "./SummaryDashboardV3.css";

const IGNORED_CENTERS_STORAGE_KEY = "summary-v3-ignored-centers";
const MAX_VISIBLE_SUMMARY_CENTERS = 7;
const NORMAL_CENTER_ROTATION_MS = 2500;
const MIN_VISIBLE_ROTATION_MS = 3000;
const WINDOW_SHIFT_ANIMATION_MS = 1560;

function readIgnoredCenters() {
  if (typeof window === "undefined") return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(IGNORED_CENTERS_STORAGE_KEY) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export default function SummaryDashboardV3({ centers = [], loading = false, onSelectCenter, cargo, translate }) {
  const labelsRevision = useLabelVersion();
  const t = useCallback((key, fallback) => translateText(translate, key, fallback), [translate]);
  const canSeeSensorFailures = useMemo(() => canViewSensorFailures(cargo), [cargo]);
  const [ignoredCenterKeys, setIgnoredCenterKeys] = useState(readIgnoredCenters);
  const [hideNormalCenters, setHideNormalCenters] = useState(false);
  const [normalRotationIndex, setNormalRotationIndex] = useState(0);
  const [windowAnimating, setWindowAnimating] = useState(false);
  const [windowShiftDirection, setWindowShiftDirection] = useState("forward");
  const previousVisibleKeysRef = useRef([]);
  const ignoredCenterSet = useMemo(() => new Set(ignoredCenterKeys), [ignoredCenterKeys]);
  const summaries = useMemo(
    () =>
      centers
        .map((center) => getCenterSummary(center, null, { includeSensorFailures: canSeeSensorFailures, translate }))
        .sort((a, b) => getCenterCriticalityScore(b) - getCenterCriticalityScore(a)),
    [centers, canSeeSensorFailures, translate, labelsRevision] // eslint-disable-line react-hooks/exhaustive-deps -- La versión invalida labels del resolvedor en memoria.
  );
  const activeSummaries = useMemo(
    () => summaries.filter((summary) => !ignoredCenterSet.has(summary.centerKey)),
    [summaries, ignoredCenterSet]
  );
  const orderedActiveSummaries = useMemo(
    () =>
      activeSummaries
        .map((summary, index) => ({ summary, index }))
        .sort((a, b) => {
          const rankDelta = getMapCenterRank(a.summary) - getMapCenterRank(b.summary);
          if (rankDelta) return rankDelta;
          const scoreDelta = getCenterCriticalityScore(b.summary) - getCenterCriticalityScore(a.summary);
          if (scoreDelta) return scoreDelta;
          return a.index - b.index;
        })
        .map(({ summary }) => summary),
    [activeSummaries]
  );
  const nonNormalSummaries = useMemo(
    () => orderedActiveSummaries.filter((summary) => !isCenterHealthy(summary)),
    [orderedActiveSummaries]
  );
  const displayCandidateSummaries = useMemo(
    () => (hideNormalCenters ? nonNormalSummaries : orderedActiveSummaries),
    [hideNormalCenters, nonNormalSummaries, orderedActiveSummaries]
  );
  const canNavigateCenters = !hideNormalCenters && displayCandidateSummaries.length > MAX_VISIBLE_SUMMARY_CENTERS;
  const visibleSummaries = useMemo(() => {
    if (hideNormalCenters) return nonNormalSummaries;

    if (displayCandidateSummaries.length <= MAX_VISIBLE_SUMMARY_CENTERS) return displayCandidateSummaries;

    const startIndex = normalRotationIndex % displayCandidateSummaries.length;
    return Array.from({ length: MAX_VISIBLE_SUMMARY_CENTERS }, (_, offset) => {
      const nextIndex = (startIndex + offset) % displayCandidateSummaries.length;
      return displayCandidateSummaries[nextIndex];
    });
  }, [displayCandidateSummaries, hideNormalCenters, nonNormalSummaries, normalRotationIndex]);
  const enteringCenterKeys = useMemo(() => {
    const previousVisibleKeys = new Set(previousVisibleKeysRef.current);
    return new Set(
      visibleSummaries
        .map((summary) => summary.centerKey)
        .filter((centerKey) => !previousVisibleKeys.has(centerKey))
    );
  }, [visibleSummaries]);

  useEffect(() => {
    previousVisibleKeysRef.current = visibleSummaries.map((summary) => summary.centerKey);
  }, [visibleSummaries]);

  const visibleWindowLabel = useMemo(
    () =>
      `${visibleSummaries.length} ${t("summary_v3_of", "de")} ${activeSummaries.length} ${t(
        "centers",
        "centros"
      )}`,
    [activeSummaries.length, t, visibleSummaries.length]
  );

  const globalStats = useMemo(() => getSummaryDashboardStats(activeSummaries, translate), [activeSummaries, translate]);
  const distribution = useMemo(() => getSimpleDistribution(activeSummaries), [activeSummaries]);
  const handleToggleNormalCenters = useCallback(() => {
    setHideNormalCenters((current) => !current);
  }, []);
  const startWindowShiftAnimation = useCallback((direction) => {
    setWindowShiftDirection(direction);
    setWindowAnimating(true);
  }, []);
  const handlePreviousCenters = useCallback(() => {
    if (!canNavigateCenters) return;
    startWindowShiftAnimation("backward");
    setNormalRotationIndex((current) => {
      const total = displayCandidateSummaries.length;
      return total ? (current - 1 + total) % total : 0;
    });
  }, [canNavigateCenters, displayCandidateSummaries.length, startWindowShiftAnimation]);
  const handleNextCenters = useCallback(() => {
    if (!canNavigateCenters) return;
    startWindowShiftAnimation("forward");
    setNormalRotationIndex((current) => {
      const total = displayCandidateSummaries.length;
      return total ? (current + 1) % total : 0;
    });
  }, [canNavigateCenters, displayCandidateSummaries.length, startWindowShiftAnimation]);

  useEffect(() => {
    setNormalRotationIndex(0);
  }, [hideNormalCenters, displayCandidateSummaries.length]);

  useEffect(() => {
    if (!windowAnimating) return undefined;

    const timeout = window.setTimeout(() => {
      setWindowAnimating(false);
    }, WINDOW_SHIFT_ANIMATION_MS);

    return () => window.clearTimeout(timeout);
  }, [windowAnimating]);

  useEffect(() => {
    if (hideNormalCenters) return undefined;

    if (displayCandidateSummaries.length <= MAX_VISIBLE_SUMMARY_CENTERS) return undefined;

    const interval = window.setInterval(() => {
      startWindowShiftAnimation("forward");
      setNormalRotationIndex((current) => current + 1);
    }, Math.max(NORMAL_CENTER_ROTATION_MS, MIN_VISIBLE_ROTATION_MS));

    return () => window.clearInterval(interval);
  }, [displayCandidateSummaries.length, hideNormalCenters, startWindowShiftAnimation]);

  const handleToggleIgnoredCenter = useCallback((centerKey) => {
    setIgnoredCenterKeys((current) => {
      const nextSet = new Set(current);
      if (nextSet.has(centerKey)) nextSet.delete(centerKey);
      else nextSet.add(centerKey);
      const next = Array.from(nextSet);
      if (typeof window !== "undefined") {
        window.localStorage.setItem(IGNORED_CENTERS_STORAGE_KEY, JSON.stringify(next));
      }
      return next;
    });
  }, []);

  const openCenter = useCallback((summary) => {
    if (!summary?.center?.centro || !summary?.center?.area || !summary?.center?.region) return;
    onSelectCenter?.({
      centro: summary.center.centro,
      area: summary.center.area,
      region: summary.center.region,
    });
  }, [onSelectCenter]);

  if (loading && !summaries.length) {
    return (
      <section className="summary-v3 summary-v3--loading">
        <div className="summary-v3__empty">
          {t("summary_v3_loading", "Cargando resumen operacional...")}
        </div>
      </section>
    );
  }

  return (
    <section className="summary-v3" aria-label={t("summary_v3_aria_label", "Resumen operacional simple")}>
      <SummaryV3Kpis stats={globalStats} canSeeSensorFailures={canSeeSensorFailures} translate={translate} />
      <div className="summary-v3__mainGrid">
        <SummaryV3CenterTable
          summaries={visibleSummaries}
          visibleWindowLabel={visibleWindowLabel}
          canNavigateCenters={canNavigateCenters}
          enteringCenterKeys={enteringCenterKeys}
          windowAnimating={windowAnimating}
          windowShiftDirection={windowShiftDirection}
          hideNormalCenters={hideNormalCenters}
          onPreviousCenters={handlePreviousCenters}
          onNextCenters={handleNextCenters}
          onToggleNormalCenters={handleToggleNormalCenters}
          onOpenCenter={openCenter}
          translate={translate}
        />
      </div>
      <div className="summary-v3__bottomGrid">
        <SummaryV3CageMap
          summaries={visibleSummaries}
          canNavigateCenters={canNavigateCenters}
          enteringCenterKeys={enteringCenterKeys}
          windowAnimating={windowAnimating}
          windowShiftDirection={windowShiftDirection}
          hideNormalCenters={hideNormalCenters}
          onPreviousCenters={handlePreviousCenters}
          onNextCenters={handleNextCenters}
          onToggleNormalCenters={handleToggleNormalCenters}
          canSeeSensorFailures={canSeeSensorFailures}
          ignoredCenterKeys={ignoredCenterKeys}
          onToggleIgnoredCenter={handleToggleIgnoredCenter}
          translate={translate}
        />
        <SummaryV3Distribution distribution={distribution} total={globalStats.totalCages} translate={translate} />
      </div>
    </section>
  );
}
